import type { Activity } from '$lib/types';
import type { HappyRow } from '$lib/api/completionTypes';
import type { CareContact, CareIssue } from '$lib/api/customerCare';
import { groupQueue, queueScores, type QueueReason } from './happyCode.ts';
import { istDay, istTime, nextWorkingDay, touchpointTypeLabel } from './touchpoints.ts';

/**
 * One list of everyone the coordinator has to ring, whatever the reason.
 * Each entry keeps its source record, because the outcome is always written
 * back through that record's own workflow: feedback queue
 * (/happy-code/queue/:id/action), case customer call (/issues/:id/customer-call)
 * or a touchpoint on the contact (/activities…).
 */
export type CallKind = 'unhappy' | 'fix' | 'planned' | 'callback' | 'happy' | 'reconnect' | 'feedback' | 'check';
export const callKindOrder: readonly CallKind[] = ['unhappy', 'fix', 'planned', 'callback', 'happy', 'reconnect', 'feedback', 'check'];
export type CallTone = 'danger' | 'info' | 'warn' | 'ok' | 'muted';
export const callKinds: Record<CallKind, { label: string; plural: string; tone: CallTone; prompt: string; rule: string }> = {
  unhappy: {
    label: 'Unhappy customer', plural: 'Unhappy', tone: 'danger',
    prompt: 'Listen first. What went wrong, and what would make it right? Agree a plan and a date.',
    rule: 'Call the same day — at the latest the next working day.'
  },
  fix: {
    label: 'Confirm the fix', plural: 'Confirm fix', tone: 'info',
    prompt: 'Is the problem fixed for you? Only the customer’s yes closes the case.',
    rule: 'A resolved case stays open until the customer confirms.'
  },
  planned: {
    label: 'Planned call', plural: 'Planned', tone: 'warn',
    prompt: 'You booked this call. Check the agenda, then record what was agreed and the next step.',
    rule: 'Calls you planned for today or earlier.'
  },
  callback: {
    label: 'Call back', plural: 'Call backs', tone: 'warn',
    prompt: 'They asked you to call back. Pick up where the last conversation left off.',
    rule: 'Follow-ups due on conversations you logged.'
  },
  happy: {
    label: 'Happy call', plural: 'Happy calls', tone: 'ok',
    prompt: 'Is everything working as expected? Anything to come back for? If they’re happy, ask for a Google review.',
    rule: '7 days after feedback (Monday if that falls on a Sunday).'
  },
  reconnect: {
    label: 'Reconnect', plural: 'Reconnect', tone: 'muted',
    prompt: 'Nobody has spoken to them in a while. Check in: any new projects, sites or meetings to set up?',
    rule: 'Business contacts nobody has spoken to recently.'
  },
  feedback: {
    label: 'Feedback not in', plural: 'Feedback', tone: 'info',
    prompt: 'Installed, but no feedback yet. Ask how it went and share the feedback link.',
    rule: 'Installed or serviced with no feedback form yet.'
  },
  check: {
    label: 'Details to check', plural: 'Checks', tone: 'muted',
    prompt: 'Confirm who the customer is and which number to keep before anything is merged.',
    rule: 'New numbers and old customers to confirm.'
  }
};
const happyKind: Record<QueueReason, CallKind> = { negatives: 'unhappy', due_calls: 'happy', feedback_pending: 'feedback', phone_mismatch: 'check', old_customers: 'check' };
const reasonText: Record<QueueReason, string> = {
  negatives: 'Low feedback score', due_calls: 'Day-7 happy call', feedback_pending: 'No feedback yet',
  phone_mismatch: 'New phone number to check', old_customers: 'Old customer to confirm'
};

export interface CallEntry {
  /** Presentation identity only; never used to link or mutate CRM records. */
  companyId?: number | null;
  companyName?: string | null;
  verticalId?: number | null;
  /** Validated presentation person, separate from the workflow action's personId. */
  groupingPersonId?: number | null;
  key: string;
  kind: CallKind;
  name: string;
  /** Site, company or team: where this customer is. */
  place: string;
  /** Why they are in the queue, in a few words. */
  reason: string;
  /** What the customer or the last conversation said. */
  quote: string;
  scores: Array<{ label: string; score: number; low: boolean }>;
  /** IST day the call became due; null means "now". */
  day: string | null;
  /** Last IST day the call is on time (unhappy customers get one extra working day). */
  deadline: string | null;
  time: string | null;
  /** Known number; empty when it has to be looked up on the contact. */
  phone: string;
  personId: number | null;
  happy?: { row: HappyRow; reasons: QueueReason[] };
  issue?: CareIssue;
  activity?: Activity;
  contact?: CareContact;
}

// Missing pipeline metadata fails closed instead of merging across pipelines.
function positiveId(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null;
}

function identity(row: { company_id?: number | null; vertical_id?: number | null; company_name?: string | null }) {
  return { companyId: positiveId(row.company_id), verticalId: positiveId(row.vertical_id), companyName: row.company_name ?? null };
}

export interface CallGroup {
  key: string;
  name: string;
  entries: CallEntry[];
  /** Highest-priority matching child; actions must still target a child. */
  primary: CallEntry;
}

function compareCalls(a: CallEntry, b: CallEntry, now: string): number {
  const today = istDay(now);
  return callKindOrder.indexOf(a.kind) - callKindOrder.indexOf(b.kind) ||
    (a.day ?? today).localeCompare(b.day ?? today) ||
    (a.time ?? '').localeCompare(b.time ?? '') || a.key.localeCompare(b.key);
}

/** Group only explicit account/person IDs within a known pipeline. Keep every source. */
export function groupCallQueue(entries: CallEntry[], now = new Date().toISOString()): CallGroup[] {
  const groups = new Map<string, CallGroup>();
  for (const entry of [...entries].sort((a, b) => compareCalls(a, b, now))) {
    const companyId = positiveId(entry.companyId);
    // Happy workflow links may be stale/foreign. Never use them as a fallback,
    // including when an older server has not supplied queue identity metadata.
    const personId = positiveId(entry.happy ? entry.groupingPersonId : entry.personId);
    const verticalId = positiveId(entry.verticalId);
    const key = verticalId && (companyId || personId)
      ? `pipeline:${verticalId}:${companyId ? `company:${companyId}` : `person:${personId}`}`
      : `entry:${entry.key}`;
    const group = groups.get(key);
    if (group) group.entries.push(entry);
    else groups.set(key, { key, name: entry.companyId ? `Company #${entry.companyId}` : entry.name, entries: [entry], primary: entry });
  }
  return [...groups.values()].map(group => ({ ...group, name: group.entries.length === 1 ? group.primary.name : group.entries.find(e => e.companyName?.trim())?.companyName?.trim() || group.name }));
}

/** Match children, but retain the entire account history and source action objects. */
export function filterCallGroups(groups: CallGroup[], filter: 'all' | CallKind, search = '', now = new Date().toISOString()): CallGroup[] {
  const needle = search.trim().toLowerCase();
  return groups.flatMap(group => {
    const matches = group.entries.filter(e => {
      if (filter !== 'all' && e.kind !== filter) return false;
      const row = e.happy?.row;
      const text = [group.name, e.name, e.place, e.reason, e.quote, e.phone, e.key,
        callKinds[e.kind].label, row?.name, row?.customer_name, row?.order_number,
        row?.issue_id ? `Case #${row.issue_id}` : '', row?.deal_id ? `Order #${row.deal_id}` : '',
        e.companyName, e.issue?.reporter_name].filter(Boolean).join(' ').toLowerCase();
      return !needle || text.includes(needle);
    });
    return matches.length ? [{ ...group, primary: [...matches].sort((a, b) => compareCalls(a, b, now))[0] }] : [];
  }).sort((a, b) => compareCalls(a.primary, b.primary, now));
}

function dayOf(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.length === 10 ? value : istDay(value);
}

export function buildCallQueue(
  sources: { happy?: Partial<Record<QueueReason, HappyRow[]>>; fixes?: CareIssue[]; mine?: Activity[]; quiet?: CareContact[] },
  now = new Date().toISOString()
): CallEntry[] {
  const entries: CallEntry[] = [];
  const people = new Set<number>();
  for (const { row, reasons } of groupQueue(sources.happy)) {
    const kind = happyKind[reasons[0]];
    if (row.person_id) people.add(row.person_id);
    const day = dayOf(row.next_call_at ?? row.call_due_at ?? (kind === 'unhappy' ? row.submitted_at : null));
    const reference = row.issue_id ? `Case #${row.issue_id}` : row.order_number?.trim() ? `Order ${row.order_number.trim()}` : row.deal_id ? `Order #${row.deal_id}` : `Feedback #${row.id}`;
    entries.push({
      key: `happy:${row.id}`, kind,
      ...identity({ company_id: row.queue_company_id, vertical_id: row.vertical_id, company_name: row.queue_company_name }),
      groupingPersonId: positiveId(row.queue_person_id),
      name: row.customer_name?.trim() || row.name?.trim() || reference,
      place: [row.site, row.city].filter(Boolean).join(' · '),
      reason: [...reasons.map(r => reasonText[r]), ...(reasons.includes('feedback_pending') ? [reference] : [])].join(' · '),
      quote: row.comment?.trim() || row.pending_reason?.trim() || '',
      scores: queueScores(row),
      day, deadline: day && kind === 'unhappy' && !row.next_call_at ? nextWorkingDay(day) : day, time: null,
      phone: row.verified_phone || row.alternate_phone || row.phone || '',
      personId: row.person_id ?? null,
      happy: { row, reasons }
    });
  }
  for (const issue of sources.fixes ?? []) {
    const day = dayOf(issue.customer_followup_at ?? issue.resolved_at);
    entries.push({
      key: `fix:${issue.id}`, kind: 'fix',
      ...identity(issue),
      name: issue.customer_name || issue.company_name || issue.reporter_name || `Case #${issue.id}`,
      place: [issue.location, issue.team_name].filter(Boolean).join(' · '),
      reason: `Case #${issue.id} · ${issue.title}`,
      quote: issue.resolving_technician_name ? `Fixed by ${issue.resolving_technician_name}` : '',
      scores: [], day, deadline: day, time: null,
      phone: issue.reporter_phone || '', personId: issue.person_id ?? null, issue
    });
  }
  // A plan beats a reminder for the same contact: one call, not two.
  const mine = (sources.mine ?? []).filter(a => a.type !== 'email' && a.type !== 'meeting' && a.type !== 'visit');
  const planned = new Set(mine.filter(a => a.status === 'scheduled' && a.person_id).map(a => a.person_id));
  for (const a of mine) {
    const isPlan = a.status === 'scheduled';
    if (!isPlan && a.person_id && planned.has(a.person_id)) continue;
    const day = dayOf(isPlan ? a.scheduled_at ?? a.due_at : a.next_action_date?.slice(0, 10));
    if (a.person_id) people.add(a.person_id);
    entries.push({
      key: `activity:${a.id}`, kind: isPlan ? 'planned' : 'callback',
      ...identity(a),
      name: a.person_name || a.company_name || a.title || 'Contact',
      place: a.person_name ? a.company_name ?? '' : '',
      reason: isPlan ? `${touchpointTypeLabel(a.type)} · ${a.title}` : a.next_action || 'Follow up',
      quote: isPlan ? (a.body && a.body !== a.title ? a.body : '') : a.body || a.title || '',
      scores: [], day, deadline: day,
      time: isPlan && a.scheduled_at ? istTime(a.scheduled_at) : null,
      phone: '', personId: a.person_id ?? null, activity: a
    });
  }
  for (const c of sources.quiet ?? []) {
    if (people.has(c.id)) continue;
    entries.push({
      key: `reconnect:${c.id}`, kind: 'reconnect',
      ...identity(c),
      name: `${c.first_name} ${c.last_name}`.trim() || `Contact #${c.id}`,
      place: c.team_name,
      reason: c.last_spoken_at ? `Last spoken ${c.days_since_spoken ?? '?'} days ago` : 'Never spoken to',
      quote: '', scores: [], day: null, deadline: null, time: null,
      phone: c.phone || '', personId: c.id, contact: c
    });
  }
  const today = istDay(now);
  const rank = (e: CallEntry) => callKindOrder.indexOf(e.kind);
  return entries.sort((a, b) =>
    rank(a) - rank(b) ||
    (a.day ?? today).localeCompare(b.day ?? today) ||
    (a.time ?? '').localeCompare(b.time ?? '') ||
    a.key.localeCompare(b.key));
}

/** Late once the deadline day has passed (IST). */
export function isLate(entry: Pick<CallEntry, 'deadline'>, now = new Date().toISOString()): boolean {
  return !!entry.deadline && entry.deadline < istDay(now);
}
export function telHref(phone: string): string { return `tel:${phone.replace(/[^\d+]/g, '')}`; }
export function whatsappHref(phone: string): string { return `https://wa.me/${phone.replace(/\D/g, '')}`; }
